export const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'https://valleyapi-dr9y.onrender.com'
).replace(/\/+$/, '');

const DEFAULT_TIMEOUT_MS = 15000;

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

// Wraps an optional caller-provided AbortSignal (e.g. one tied to a
// component unmounting) with our own timeout-driven abort, so a request that
// never gets a response (server accepted the connection but hung) doesn't
// leave a submit button stuck in "loading" forever.
function createRequestSignal(externalSignal, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  let timedOut = false;

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  return {
    signal: controller.signal,
    finish: () => clearTimeout(timeoutId),
    didTimeOut: () => timedOut,
  };
}

function timeoutError(message) {
  const error = new Error(message);
  error.name = 'TimeoutError';
  return error;
}

export async function verifyEmailToken(token, { fetchImpl = fetch, signal } = {}) {
  const request = createRequestSignal(signal);
  let response;
  try {
    response = await fetchImpl(`${API_BASE_URL}/api/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/html' },
      body: JSON.stringify({ token }),
      signal: request.signal,
    });
  } catch (error) {
    if (request.didTimeOut()) {
      throw timeoutError('The verification request timed out. Please check your connection and try again.');
    }
    throw error;
  } finally {
    request.finish();
  }

  if (!response.ok) {
    const error = new Error(
      response.status === 400
        ? 'Invalid or expired token.'
        : 'The verification service is temporarily unavailable.'
    );
    error.status = response.status;
    throw error;
  }

  return true;
}

export async function resendVerificationEmail(email, { fetchImpl = fetch, signal } = {}) {
  const request = createRequestSignal(signal);
  let response;
  try {
    response = await fetchImpl(`${API_BASE_URL}/api/send-confirmation-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      signal: request.signal,
    });
  } catch (error) {
    if (request.didTimeOut()) {
      throw timeoutError('The request timed out. Please check your connection and try again.');
    }
    throw error;
  } finally {
    request.finish();
  }

  const body = await readJson(response);

  if (!response.ok) {
    throw new Error(body.error || 'The verification email could not be sent.');
  }

  return body;
}
